import type { EdpBlock } from '@/services/api/edp/edp';
import { fetchApi } from '@/services/api-client';

export const CHECKOUT_PATH = '/admin/attendance/checkout';

export type CheckoutCallStatus = 'waiting' | 'on_way' | 'collected' | 'cancelled';

/**
 * One student's checkout call. This is the exact shape the API both returns from `calls()`/
 * `call()`/`onWay()`/`collected()`/`cancel()` AND pushes as the `checkout.call` socket event
 * (mirrors `CheckoutCallEvent` in risp-api's realtime.events.ts) — kept as one declaration on
 * purpose so a screen never has to reconcile a fetched version against a pushed one.
 */
export interface CheckoutCall {
  id: string;
  studentId: string;
  displayName: string;
  fullName: string | null;
  gradeLevel: number;
  photoUrl: string;
  status: CheckoutCallStatus;
  calledAt: string;
  onWayAt: string | null;
  collectedAt: string | null;
  /** Everyone entitled to collect this child — carried on the call itself so a row still knows
   *  who may take them home after a refresh, on a second screen, or off a realtime push, none of
   *  which have anywhere else to read it from. */
  guardianNames: string[];
  /** Nobody is collecting this child: they are walking home on their own. On a classroom card it
   *  is the whole point — the teacher is sending a child to a door where no one is waiting, which
   *  otherwise looks like a mistake. At the door it changes the last button's wording. */
  selfDismissed: boolean;
}

/**
 * Why a child cannot be called right now, or null if they can. Mirrors `UnavailableReason` in
 * risp-api's checkout-candidates.resolve-unavailable.ts.
 */
export type CheckoutUnavailableReason = 'absent' | 'excused' | 'early_dismissal' | 'collected' | 'edp' | 'suspended';

/** The door's one-day note that a child is not going to EDP today, with who wrote it down. */
export interface CheckoutEdpExcusal {
  id: string;
  reason: string;
  recordedByName: string;
}

/**
 * What EDP still holds over a child this afternoon. The door and the register both ask it of the
 * same child on the same day, so they read one declaration rather than two that could drift.
 */
export interface CheckoutEdpState {
  /** The block their afternoon is spoken for by, or null when no seat holds them today. */
  edpBlock: EdpBlock | null;
  /** When that block lets them go, `HH:MM`. Null for a block the school advertises no end for. */
  edpUntil: string | null;
  edpExcusal: CheckoutEdpExcusal | null;
  /** Their block has ended, or the EDP register says they were never there. A freed child still
   *  shows the block they were in, so this sits beside `edpBlock` rather than clearing it. */
  edpReleased: boolean;
}

/**
 * Whether a child may walk home, and whether they are doing so today. Both the door's search
 * results and the whole-school register answer this for the same child on the same day, so they
 * read one declaration rather than two that could drift.
 */
export interface CheckoutSelfDismissalState {
  /** Their parents have signed the form letting them leave campus unaccompanied. A standing
   *  permission, not a plan — a permitted child is still collected on plenty of days. */
  canLeaveAlone: boolean;
  /** Set when today is one of those days. The id, not a boolean, because the undo is a DELETE on
   *  that row and the door is the only screen that knows which child it is looking at. */
  selfDismissalExceptionId: string | null;
}

export interface CheckoutCandidate extends CheckoutEdpState, CheckoutSelfDismissalState {
  studentId: string;
  displayName: string;
  fullName: string | null;
  /** Null when the student record has no grade on file — rendered as no grade rather than as
   *  Pre-K 2, which is what grade 0 means here. Such a child cannot be called; the API refuses
   *  with a message naming the problem, and the door toasts it. */
  gradeLevel: number | null;
  photoUrl: string;
  callId: string | null;
  status: CheckoutCallStatus | null;
  /** When they were handed over. The door shows it beside an Undo action, so reception reversing
   *  a mis-tapped Collected can see which of two handovers they are actually undoing. */
  collectedAt: string | null;
  unavailableReason: CheckoutUnavailableReason | null;
  /** Everyone entitled to collect this child, regardless of which guardian's card it's on. */
  guardianNames: string[];
}

/**
 * Where one child stands at the end of the day. Mirrors `RosterState` in risp-api's
 * checkout-roster-state.ts: the call statuses and the reasons a child cannot be called, folded
 * into the single chip a register row shows, plus `in_class` for a child neither axis describes.
 */
export type CheckoutRosterState = 'in_class' | 'waiting' | 'on_way' | 'collected' | CheckoutUnavailableReason;

export interface CheckoutRosterStudent extends CheckoutEdpState, CheckoutSelfDismissalState {
  studentId: string;
  displayName: string;
  fullName: string | null;
  gradeLevel: number | null;
  photoUrl: string;
  state: CheckoutRosterState;
  calledAt: string | null;
  collectedAt: string | null;
}

/**
 * One guardian's card: only the children actually linked to that guardian, never merged across
 * guardians who happen to share a child with someone else's other child (a safeguarding
 * boundary, not a display choice — see checkout-candidates.service.ts).
 */
export interface CandidateFamily {
  parentId: string | null;
  parentNames: string[];
  children: CheckoutCandidate[];
}

export const checkoutApi = {
  candidates: (q: string, date: string) => {
    const qs = new URLSearchParams({ q, date });
    return fetchApi<CandidateFamily[]>(`${CHECKOUT_PATH}/candidates?${qs.toString()}`);
  },

  /**
   * Omitting `grades` asks for every grade; passing an EMPTY array asks for none, and sends
   * `grades=` to say so. The API keeps those two apart deliberately (see parseGradesParam there)
   * — collapsing an empty selection into "no filter" is how a classroom screen ended up showing
   * the whole school.
   */
  calls: (date: string, grades?: number[]) => {
    const qs = new URLSearchParams({ date });
    if (grades) qs.set('grades', grades.join(','));
    return fetchApi<CheckoutCall[]>(`${CHECKOUT_PATH}/calls?${qs.toString()}`);
  },

  /** Every active student and their state for the day. Uncapped, unlike `candidates`. */
  roster: (date: string) => {
    const qs = new URLSearchParams({ date });
    return fetchApi<CheckoutRosterStudent[]>(`${CHECKOUT_PATH}/roster?${qs.toString()}`);
  },

  /**
   * `selfDismissed` says this child is walking home rather than being collected. It is still an
   * ordinary call — the classroom sees the card and sends them down — and the API checks it
   * against the child's standing permission, refusing one they do not have.
   */
  call: (studentId: string, date: string, selfDismissed = false) =>
    fetchApi<CheckoutCall>(`${CHECKOUT_PATH}/calls`, {
      method: 'POST',
      body: JSON.stringify({ studentId, date, selfDismissed }),
    }),

  onWay: (id: string) => fetchApi<CheckoutCall>(`${CHECKOUT_PATH}/calls/${id}/on-way`, { method: 'POST' }),

  collected: (id: string) => fetchApi<CheckoutCall>(`${CHECKOUT_PATH}/calls/${id}/collected`, { method: 'POST' }),

  // No actor argument — cancelling records nothing about who reversed the mistake.
  cancel: (id: string) => fetchApi<CheckoutCall>(`${CHECKOUT_PATH}/calls/${id}/cancel`, { method: 'POST' }),

  /** One day's note that a child is not going to EDP. It never touches their seat or the round. */
  excuseFromEdp: (studentId: string, date: string, reason: string) =>
    fetchApi<{ id: string; studentId: string; date: string; reason: string }>(`${CHECKOUT_PATH}/edp-excusals`, {
      method: 'POST',
      body: JSON.stringify({ studentId, date, reason }),
    }),

  unexcuseFromEdp: (id: string) => fetchApi<void>(`${CHECKOUT_PATH}/edp-excusals/${id}`, { method: 'DELETE' }),

  /** A parent is coming for a child who would otherwise walk home. One day only, and nothing
   *  else about the child changes: they stay in school, and stay callable the ordinary way. */
  keepForPickup: (studentId: string, date: string) =>
    fetchApi<{ id: string; studentId: string; date: string }>(`${CHECKOUT_PATH}/self-dismissal-exceptions`, {
      method: 'POST',
      body: JSON.stringify({ studentId, date }),
    }),

  undoKeepForPickup: (id: string) =>
    fetchApi<void>(`${CHECKOUT_PATH}/self-dismissal-exceptions/${id}`, { method: 'DELETE' }),
};
